import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { ProgressCard as ProgressCardType, DefaultProgressCardProps } from '../types';
import { DefaultProgressCardItem } from './ProgressCardItem';
import { EmailTemplatePreviewDialog } from 'app/NewAIAvatarLayout/sharedComponents/EmailTemplatePreviewDialog';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../../redux/headerSlice/selectors';

const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : (theme.palette.background.card || theme.palette.grey[50]),
  borderRadius: 'calc(4px + 0.625rem)',
  padding: theme.spacing(3),
  boxShadow: theme.palette.mode === 'dark' ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  fontWeight: 600,
  lineHeight: 1.3,
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
}));

const PreviewIconButton = styled(IconButton)(({ theme }) => ({
  width: '24px',
  height: '24px',
  minWidth: '24px',
  minHeight: '24px',
  padding: '4px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.primary.main,
  },
}));

const ItemsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

export const DefaultProgressCard: React.FC<DefaultProgressCardProps> = ({ card }) => {
  const { title, items, variant = 'default', entity_type, entity_id } = card;
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Get selected dealer
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';
  
  // Check if this is an EMAIL_TEMPLATE entity (case-insensitive)
  const entityIdStr = entity_id ? String(entity_id).trim() : '';
  const isEmailTemplate = entity_type && entity_type.toUpperCase() === 'EMAIL_TEMPLATE' && entityIdStr !== '';
  
  // Debug logging
  React.useEffect(() => {
    if (entity_type || entity_id) {
      console.log('DefaultProgressCard - entity_type:', entity_type, 'entity_id:', entity_id, 'entityIdStr:', entityIdStr, 'isEmailTemplate:', isEmailTemplate, 'card:', card);
    }
  }, [entity_type, entity_id, entityIdStr, isEmailTemplate, card]);
  
  // Handle preview button click
  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEmailTemplate && currentDealerId) {
      setPreviewOpen(true);
    } else {
      console.warn('Preview not available:', { isEmailTemplate, currentDealerId, entity_type, entity_id });
    }
  };
  
  // Close preview modal
  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  if (!title && (!items || items.length === 0)) {
    return null;
  }

  return (
    <>
      <CardContainer>
        {title && (
          <TitleContainer>
            <TitleText>
              {title}
            </TitleText>
            {isEmailTemplate && (
              <Tooltip title="Preview Email Template" arrow>
                <PreviewIconButton
                  size="small"
                  onClick={handlePreviewClick}
                  aria-label="Preview email template"
                >
                  <Visibility sx={{ fontSize: 18 }} />
                </PreviewIconButton>
              </Tooltip>
            )}
          </TitleContainer>
        )}
        {!title && isEmailTemplate && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Tooltip title="Preview Email Template" arrow>
              <PreviewIconButton
                size="small"
                onClick={handlePreviewClick}
                aria-label="Preview email template"
              >
                <Visibility sx={{ fontSize: 18 }} />
              </PreviewIconButton>
            </Tooltip>
          </Box>
        )}
        
        {items && items.length > 0 && (
          <ItemsContainer>
            {items
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((item, index) => (
                <DefaultProgressCardItem 
                  key={index} 
                  item={item} 
                />
              ))}
          </ItemsContainer>
        )}
      </CardContainer>
      
      {/* Preview Dialog */}
      {isEmailTemplate && (
        <EmailTemplatePreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          templateId={entityIdStr}
          dealerId={currentDealerId}
        />
      )}
    </>
  );
};

